'use client'

import { AnimatePresence, motion } from 'motion/react'
import { PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePathname } from 'next/navigation'

import { toastStore } from '@/store/ui/toast.store'

export const ToastViewport = () => {
  const toasts = toastStore((state) => state.toasts)
  const removeToast = toastStore((state) => state.removeToast)
  const clearToasts = toastStore((state) => state.clear)
  const pathname = usePathname()

  useEffect(() => {
    clearToasts()
  }, [pathname, clearToasts])

  return (
    <ToastPortal>
      <div className="pointer-events-none fixed inset-0 z-[9999] flex flex-col items-center justify-end gap-[8px] px-[20px] pb-[96px]">
        <AnimatePresence initial={false}>
          {toasts.map(({ id, description, second }) => (
            <ToastItem key={id} toastId={id} second={second} removeToast={removeToast}>
              {description}
            </ToastItem>
          ))}
        </AnimatePresence>
      </div>
    </ToastPortal>
  )
}

const ToastItem = ({
  children,
  second,
  removeToast,
  toastId,
}: {
  children: ReactNode
  second: number
  removeToast: (id: string) => void
  toastId: string
}) => {
  useEffect(() => {
    const timerId = window.setTimeout(() => {
      removeToast(toastId)
    }, second * 1000)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [second, removeToast, toastId])

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      layout
      className="pointer-events-auto w-full max-w-[472px] rounded-[4px] bg-black/80 py-[10px] px-[12px] text-center bg-color-gray-8"
    >
      <div className="text-body2_strong text-white">{children}</div>
    </motion.div>
  )
}

const ToastPortal = ({ children }: PropsWithChildren) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const toastRoot = mounted ? document.getElementById('toast-root') : null
  const el = useMemo(() => {
    if (!mounted) return null
    return document.createElement('div')
  }, [mounted])

  useEffect(() => {
    if (!mounted || !toastRoot || !el) return

    toastRoot.appendChild(el)

    return () => {
      toastRoot.removeChild(el)
    }
  }, [mounted, toastRoot, el])

  if (!mounted || !toastRoot || !el) return null

  return createPortal(children, el)
}

export default ToastViewport
